import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ConsentBanner } from "@/components/ConsentBanner";
import { NewsletterModal } from "@/components/NewsletterModal";
import { AlertTriangle, X, MessageSquare, Send } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { reportIssue } from "@/lib/report-issue.functions";
import { useRouterState } from "@tanstack/react-router";
import { inferPageType, setPageContext } from "@/lib/analytics";

function BetaBanner() {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [description, setDescription] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitReport = useServerFn(reportIssue);

  const { a, b } = useMemo(() => ({ a: 4, b: 5 }), []);
  const canSend = description.trim().length > 0 && Number(captcha) === a + b;

  if (!open) return null;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await submitReport({
        data: {
          description: description.trim(),
          captchaAnswer: Number(captcha),
          captchaExpected: a + b,
          pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        },
      });
      if (!res.ok) {
        setError(res.error ?? "Could not submit. Please try again.");
        return;
      }
      setSubmitted(true);
      setDescription("");
      setCaptcha("");
      setTimeout(() => {
        setSubmitted(false);
        setExpanded(false);
      }, 1200);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full bg-[rgba(249,184,31,0.15)] text-[rgb(61,38,15)] pt-[10px]">
      <div className="container pb-2">
        <div className="flex items-center gap-3 text-[14px] font-medium leading-5">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <p className="flex-1">
            Beta Launch — This site is under active development. You may encounter issues.
          </p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium hover:underline"
          >
            <MessageSquare className="h-4 w-4" />
            Report Issue
          </button>
          <button
            type="button"
            aria-label="Dismiss beta banner"
            onClick={() => setOpen(false)}
            className="p-1 rounded hover:bg-black/5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {expanded && (
          <form onSubmit={handleSend} className="mt-3 space-y-2">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue you found..."
              rows={3}
              className="w-full rounded-md border border-[hsl(45_80%_70%)] bg-white/70 px-3 py-2 text-sm placeholder:text-[hsl(20_14%_45%)] focus:outline-none focus:ring-2 focus:ring-[hsl(45_80%_55%)]"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                What is {a} + {b}?
                <input
                  type="text"
                  inputMode="numeric"
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  className="w-14 rounded-md border border-[hsl(45_80%_70%)] bg-white/70 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(45_80%_55%)]"
                />
              </label>
              <button
                type="submit"
                disabled={!canSend || sending}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground shadow-sm transition-opacity disabled:opacity-60 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Send className="h-4 w-4" />
                {submitted ? "Sent" : sending ? "Sending…" : "Send"}
              </button>
            </div>
            {error && (
              <p className="text-xs text-red-700" role="alert">{error}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setPageContext({ page_type: inferPageType(pathname), page_path: pathname });
  }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      <BetaBanner />
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
      <ConsentBanner />
      {/* Newsletter popup temporarily hidden */}
      {false && <NewsletterModal />}
    </div>
  );
}